import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { routes } from 'src/routes';
import slugify from 'slugify';
import {
  Badge,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';

import { useCreateProjectMutation, OrganizationTier } from 'src/generated/graphql';
import { useUpgradeModal } from 'src/components/UpgradeModal';

export const CreateProject = ({
  orgSlug,
  className,
  projectCount,
  maxProjects,
  tier,
}: {
  orgSlug: string;
  className?: string;
  projectCount?: number;
  maxProjects?: number;
  tier?: OrganizationTier;
}) => {
  const [projectName, setProjectName] = useState('');
  const [error, setError] = useState<{ message: string } | null>();
  const [redirectPath, setRedirectPath] = useState<string>();

  const [createProject, { loading }] = useCreateProjectMutation();
  const { openUpgradeModal } = useUpgradeModal();

  const { isOpen, onOpen, onClose } = useDisclosure();

  // Check if user is at project limit (only for FREE tier, not LEGACY_FREE)
  const isAtLimit =
    tier === OrganizationTier.Free &&
    maxProjects !== undefined &&
    maxProjects !== -1 &&
    projectCount !== undefined &&
    projectCount >= maxProjects;

  if (redirectPath) {
    return <Redirect to={redirectPath} push />;
  }

  const isValid = projectName.trim().length > 0;

  const handleButtonClick = () => {
    if (isAtLimit) {
      openUpgradeModal('create more projects');
    } else {
      onOpen();
    }
  };

  return (
    <>
      <Tooltip
        label={isAtLimit ? `Free plan limit: ${projectCount}/${maxProjects} projects` : ''}
        isDisabled={!isAtLimit}
        hasArrow
        placement="top"
      >
        <Button
          onClick={handleButtonClick}
          _hover={{ bg: isAtLimit ? 'gray.200' : 'blue.500', color: isAtLimit ? 'gray.600' : 'white' }}
          className="py-4 px-4 mx-4 flex-shrink-0"
          size="sm"
          opacity={isAtLimit ? 0.7 : 1}
        >
          Create Project
          {isAtLimit && (
            <Badge
              colorScheme="purple"
              fontSize="0.6em"
              ml={2}
              borderRadius="full"
              px={1.5}
              verticalAlign="middle"
            >
              PRO
            </Badge>
          )}
        </Button>
      </Tooltip>
      <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick size="sm">
        <ModalOverlay />
        <ModalContent
          borderRadius="4px"
          as="form"
          onSubmit={async (e) => {
            e.preventDefault();
            const projectSlug = slugify(projectName.trim());

            if (!isValid) {
              setError({ message: 'Project name cannot be empty' });
              return;
            }

            await createProject({
              variables: {
                name: projectName.trim(),
                slug: projectSlug,
                organizationSlug: orgSlug,
              },
            }).then((response) => {
              if (
                response.data?.createProject?.__typename === 'ErrorNoDuplicates'
              ) {
                setError(response.data.createProject);
              } else if (
                response.data?.createProject?.__typename === 'ErrorProjectLimitReached'
              ) {
                // Close the modal and show upgrade modal
                onClose();
                openUpgradeModal('create more projects');
              } else {
                setRedirectPath(
                  routes.projectEditor.getPath({
                    orgSlug,
                    projectSlug,
                  }),
                );
              }
            });
          }}
        >
          <ModalHeader color="gray.800" mt="2">
            Create Project
          </ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel
                htmlFor="project-name"
                className="uppercase text-sm text-gray-500"
              >
                Project Name
              </FormLabel>
              <Input
                id="project-name"
                placeholder="Project Name"
                className="border-l border-t border-b px-2"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.currentTarget.value);
                  setError(null);
                }}
              />
            </FormControl>
            {error && (
              <div className="py-2 text-sm text-red-700">{error.message}</div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              size="sm"
              textTransform="uppercase"
              fontWeight="400"
              mr={5}
              onClick={onClose}
              color="gray.600"
            >
              Close
            </Button>
            <Button
              type="submit"
              textTransform="uppercase"
              fontWeight="400"
              colorScheme="blue"
              size="sm"
              transition="none"
              isDisabled={loading || !isValid}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreateProject;
